import { TabelHeadingTxt } from "../../../Text/TabelCommonTxt";
import plus from "../../../assets/images/Icon/plus.svg";
import ChecklistTableEnhancer, { RowDragHandleCell } from "./enhancer/ChecklistTableEnhancer";
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import React, { useEffect, useMemo, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import TableActions from "../../Roles/TableActions";
import { getApiEndPoint } from "../../../../utils/GenerateQueryString";
import { getAllchecklistMasterData, getAllchecklistMasterDataSuccess, updateChecklistTemplateOrder } from "../../../../store/actions/masterData.action";
import theme from "../../../../theme";
import { Divider, Text, useColorMode } from "theme-ui";
import toast from "react-hot-toast";

const ChecklistMasterdata = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams({
        currentPage: "1",
        limit: "15",
    });
    const limit = parseInt(searchParams.get('limit') || "15");
    const currentPage = parseInt(searchParams.get('currentPage') || "1");
    const { paginations } = useSelector((state: any) => state.pagination);
    const { loader, checklists } = useSelector((state: any) => state.masterData);
    const showInActionsValue = "all";
    const dispatch = useDispatch()
    const location = useLocation();
    const [colorMode] = useColorMode();
    const titleColor = (colorMode === "light" ? theme.colors?.secondary : "#8d8d8d") as string;
    const [data, setData] = useState<any[]>([]);
    const [refresh, setRrefresh] = useState<boolean>(false);
    // Always keep a ref to the latest data so handleDragEnd never uses a stale closure
    const dataRef = React.useRef<any[]>([]);

    useEffect(() => {
        if (checklists?.data) {
            setData(checklists.data);
            dataRef.current = checklists.data;
        }
    }, [checklists?.data]);

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        // Use the ref so we always have the freshest data (avoids stale closure)
        const latestData = dataRef.current;

        // Read fresh pagination values directly from searchParams
        const freshLimit = parseInt(searchParams.get('limit') || '15');
        const freshPage = parseInt(searchParams.get('currentPage') || '1');

        const oldIndex = latestData.findIndex((item: any) => item._id === active.id);
        const newIndex = latestData.findIndex((item: any) => item._id === over.id);

        if (oldIndex === -1 || newIndex === -1) return;

        const movedItems = arrayMove(latestData, oldIndex, newIndex);
        const baseIndex = (freshPage - 1) * freshLimit;
        const newItems = movedItems.map((item, index) => ({
            ...item,
            orderIndex: baseIndex + index + 1,
        }));

        // Update ref immediately so subsequent drags in the same page are accurate
        dataRef.current = newItems;

        const previousItems = checklists;
        // Optimistic Redux Update
        dispatch(getAllchecklistMasterDataSuccess({ ...checklists, data: newItems }));

        const payload = {
            sourceId: active.id,
            destinationId: over.id,
        };

        dispatch(updateChecklistTemplateOrder(payload, (res: any) => {
            if (res?.status === true) {
                toast.success('Checklist Template Order Updated Successfully!');
            } else {
                // Revert on failure
                dispatch(getAllchecklistMasterDataSuccess(previousItems));
                dataRef.current = previousItems.data;
                toast.error('Failed to update order. Reverting changes...');
            }
        }));
    };

    useEffect(() => {
        let apiEndPoint = getApiEndPoint({ limit, currentPage }, location.search, columns?.map((val: any) => ({ accessorKey: val.accessorKey, filterType: val.filterType })) || []);
        const completeURL = `${apiEndPoint}&showInActions=${showInActionsValue}`;
        dispatch(getAllchecklistMasterData(completeURL));
    }, [location?.search, limit, currentPage]);

    useEffect(() => {
        if (refresh) {
            let apiEndPoint = getApiEndPoint({ limit, currentPage }, '', columns?.map((val: any) => ({ accessorKey: val.accessorKey, filterType: val.filterType })) || []);
            const completeURL = `${apiEndPoint}&showInActions=${showInActionsValue}`;
            dispatch(getAllchecklistMasterData(completeURL));
            setRrefresh(false);
        }
    }, [refresh]);

    const handleclick = () => {
        navigate("/create-checklist")
    }
    const columns = useMemo<ColumnDef<any>[]>(() => [

        {
            header: 'No',
            enableColumnFilter: false,
            accessorKey: "no",
            cell: ({ row }) => {
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '20px', textAlign: 'left' }}>
                            <Text variant='Poppins14Normal24lh'>{((currentPage - 1) * limit) + row?.index + 1}</Text>
                        </div>
                        <RowDragHandleCell row={row} />
                    </div>
                )
            }
        },
        {
            accessorKey: "templateName",
            header: 'Template',
            cell: info => info.getValue(),
        },
        {
            accessorKey: "checklist",
            header: 'Checklist',
            cell: ({ row }) => {

                return (
                    <Text sx={{ color: titleColor, cursor: "pointer", fontWeight: 500 }}>{row?.original?.checklistItem?.length}</Text>
                )
            }
        },
        {
            accessorKey: "showInActions",
            header: 'Checklist Category',

        },
        {
            header: 'Accessor',
            accessorKey: "Accessor",
            enableColumnFilter: false,
            cell: ({ row }) => {
                return (
                    <TableActions row={row} editTo={`/edit-checklist/${row?.original?._id}`} deleteMessage='Are you sure you want to delete this template?' deleteApi={`checklist-templates-routes/deleteChecklist/${row?.original?._id}`} toggleRefresh={() => setRrefresh(true)} />
                )
            },
        }
    ], [currentPage, limit, titleColor]);
    return (
        <div className='main-content'>
            <TabelHeadingTxt Title='Checklist' Count={checklists?.count || 15} BtnTxt='Create Checklist' onClick={handleclick} src={plus} sx={{ backgroundColor: '#7743DB', color: 'white' }} />
            <Divider sx={{ color: '#D9D7DD' }} opacity={2} my={[3, 3, 4]} />
            <ChecklistTableEnhancer
                loader={loader}
                columns={columns}
                data={data}
                TotalLength={checklists?.count || 15}
                perPageCount={limit}
                currentPage={currentPage}
                onDragEnd={handleDragEnd}
                dndKey={`${currentPage}-${limit}`}
                onPerPageChange={(itemsPerPage) => {
                    const parsedQuery = Object.fromEntries(searchParams.entries());
                    setSearchParams({
                        ...parsedQuery,
                        currentPage: '1',
                        limit: itemsPerPage.toString(),
                    });
                }}
                onPageChange={(value) => {
                    const parsedQuery = Object.fromEntries(searchParams.entries());
                    setSearchParams({
                        ...parsedQuery,
                        currentPage: value.toString(),
                        limit: limit.toString(),
                    });
                }} />
        </div>
    )
}

export default ChecklistMasterdata